/* ───────────────────────────────────────────────────────────
   HolidayCalendar.jsx  –  Add • Edit/Save • Delete • Refresh
   REST  :  <API_BASE_URL>/api/holidays
   Auth  :  cookies (credentials:"include")
   ─────────────────────────────────────────────────────────── */
   import React, { useEffect, useState, useCallback } from "react";
   import PropTypes  from "prop-types";
   import Tooltip    from "@mui/material/Tooltip";
   import IconButton from "@mui/material/IconButton";
   import ModeIcon     from "@mui/icons-material/ModeOutlined";          // ✏️
   import SaveIcon     from "@mui/icons-material/SaveOutlined";          // 💾
   import AddIcon      from "@mui/icons-material/AddOutlined";           // ➕
   import RefreshIcon  from "@mui/icons-material/RefreshOutlined";       // 🗘
   import RestartIcon  from "@mui/icons-material/RestartAlt";            // ↺
   import CloseIcon    from "@mui/icons-material/CloseOutlined";         // ×
   import DeleteIcon   from "@mui/icons-material/DeleteOutlineOutlined"; // 🗑️
   
   import API_BASE_URL from "../../../../../../Constants";               // ← host + port
   
   /* ─── constants ───────────────────────────────────────────── */
   const COMPANY_ID = 1;                               // hard-wired sample
   const YEAR_NOW   = new Date().getFullYear();
   const years = (f,t)=>Array.from({length:t-f+1},(_,i)=>f+i);
   
   /* ─── helper to call JSON APIs with fetch ------------------- */
   const fetchJSON = (url, opts = {}) =>
     fetch(url, {
       credentials: "include",
       headers: { "Content-Type":"application/json" },
       ...opts,
       body: opts.body ? JSON.stringify(opts.body) : undefined
     })
     .then(async res=>{
       if(!res.ok) throw new Error(await res.text() || res.statusText);
       return res.status===204 ? null : res.json();
     });
   
   /* ─── component ───────────────────────────────────────────── */
   export default function HolidayCalendar({
     apiBaseUrl = `${API_BASE_URL}/api/holidays`,
     onClose,
   }) {
     /* state */
     const [year,   setYear]     = useState(YEAR_NOW);
     const [rows,   setRows]     = useState([]);
     const [deletedIds,setDel]   = useState([]);          // 🗑️ IDs
     const [loading,setLoad]     = useState(false);
     const [saving, setSave]     = useState(false);
     const [error,  setErr ]     = useState(null);
     const [edit,   setEdit]     = useState(false);
   
     /* endpoints */
     const listUrl = `${apiBaseUrl}/company/${COMPANY_ID}?year=${year}`;
     const seedUrl = `${apiBaseUrl}/company/${COMPANY_ID}/year/${year}`;
   
     /* load ---------------------------------------------------- */
     const loadRows = useCallback(async()=>{
       setLoad(true); setErr(null);
       try {
         const data = await fetchJSON(listUrl);
         setRows(data); setDel([]);
       } catch(e){ setErr("Failed to load."); }
       finally    { setLoad(false); }
     },[listUrl]);
   
     useEffect(()=>{ loadRows(); },[loadRows]);
   
     /* grid helpers ------------------------------------------- */
     const addRow    = ()=>{ setRows(p=>[{factor:1.5,holidayDate:"",reason:""},...p]); setEdit(true); };
     const updateRow = (i,k,v)=> setRows(p=>p.map((r,idx)=> idx===i?{...r,[k]:v}:r));
     const deleteRow = i=>()=> setRows(p=>{
       const copy=[...p]; const [victim]=copy.splice(i,1);
       if(victim?.holidayId) setDel(d=>[...d,victim.holidayId]);
       return copy;
     });
   
     /* save ---------------------------------------------------- */
     const saveGrid = async()=>{
       if(saving) return;
       setSave(true); setErr(null);
       try{
         /* deletes */
         await Promise.all(deletedIds.map(id=>fetchJSON(`${apiBaseUrl}/${id}`,{method:"DELETE"})));
   
         /* inserts / updates */
         await Promise.all(rows.map(r=>{
           /* new */
           if(!r.holidayId){
             return fetchJSON(apiBaseUrl,{
               method:"POST",
               body:{...r,companyId:COMPANY_ID}
             });
           }
           /* existing */
           return fetchJSON(`${apiBaseUrl}/${r.holidayId}`,{method:"PUT",body:r});
         }));
         await loadRows(); setEdit(false);
       }catch{ setErr("Save failed."); }
       finally{ setSave(false); }
     };
   
     /* reset defaults ----------------------------------------- */
     const resetYear = async()=>{
       if(saving) return;
       setSave(true); setErr(null);
       try{
         await fetchJSON(seedUrl,{method:"POST"});
         await loadRows();
       }catch{ setErr("Reset failed."); }
       finally{ setSave(false); }
     };
   
     /* ─── UI ───────────────────────────────────────────────── */
     return (
       <div className="p-4 flex flex-col gap-3 w-full overflow-auto">
   
         {/* toolbar */}
         <div className="flex items-center gap-3">
           {/* Edit ⇆ Save */}
           <Tooltip title={edit?"Save changes":"Edit"}><span>
             <IconButton size="small" disabled={saving}
               onClick={edit? saveGrid : ()=>setEdit(true)}>
               {edit
                 ? <SaveIcon fontSize="small" className={saving?"text-gray-400":"text-blue-600"}/>
                 : <ModeIcon fontSize="small" className="text-gray-700"/>}
             </IconButton>
           </span></Tooltip>
   
           {/* Add */}
           <Tooltip title="Add row"><span>
             <IconButton size="small" disabled={saving} onClick={addRow}>
               <AddIcon fontSize="small"/>
             </IconButton>
           </span></Tooltip>
   
           {/* Refresh */}
           <Tooltip title="Reload list"><span>
             <IconButton size="small" disabled={saving||loading} onClick={loadRows}>
               <RefreshIcon fontSize="small"/>
             </IconButton>
           </span></Tooltip>
   
           {/* Reset */}
           <Tooltip title="Create / Reset year"><span>
             <IconButton size="small" disabled={saving} onClick={resetYear}>
               <RestartIcon fontSize="small"/>
             </IconButton>
           </span></Tooltip>
   
           {/* Close */}
           {onClose && (
             <Tooltip title="Close"><span>
               <IconButton size="small" onClick={onClose}>
                 <CloseIcon fontSize="small"/>
               </IconButton>
             </span></Tooltip>
           )}
   
           {/* Year selector */}
           <div className="flex items-center gap-2 ml-6">
             <label className="font-semibold">Year</label>
             <select value={year} onChange={e=>setYear(+e.target.value)}
                     className="border rounded px-2 py-1">
               {years(YEAR_NOW-5,YEAR_NOW+5).map(y=><option key={y} value={y}>{y}</option>)}
             </select>
           </div>
   
           {/* status */}
           {(saving||error) && (
             <div className="text-xs ml-4">
               {saving && <span className="text-gray-500 mr-3">Saving…</span>}
               {error  && <span className="text-red-600">{error}</span>}
             </div>
           )}
         </div>
   
         {/* grid */}
         <div className="overflow-auto border rounded">
           {loading ? (
             <p className="p-4 text-sm text-gray-500">loading…</p>
           ) : (
             <table className="w-full text-sm">
               <thead className="bg-gray-50">
                 <tr>
                   <th className="px-2 py-1 border-b text-left">Date</th>
                   <th className="px-2 py-1 border-b text-left">Factor</th>
                   <th className="px-2 py-1 border-b text-left">Reason</th>
                   {edit && <th className="border-b" />}
                 </tr>
               </thead>
               <tbody>
                 {rows.map((r,i)=>(
                   <tr key={i} className="even:bg-gray-50">
                     <td className="border-t px-1">
                       <input type="date" value={r.holidayDate||""}
                              onChange={e=>updateRow(i,"holidayDate",e.target.value)}
                              disabled={!edit} className="w-full"/>
                     </td>
                     <td className="border-t px-1">
                       <input type="number" step="0.01" value={r.factor}
                              onChange={e=>updateRow(i,"factor",e.target.value)}
                              disabled={!edit} className="w-20"/>
                     </td>
                     <td className="border-t px-1">
                       <input type="text" value={r.reason}
                              onChange={e=>updateRow(i,"reason",e.target.value)}
                              disabled={!edit} className="w-full"/>
                     </td>
                     {edit && (
                       <td className="border-t px-1 text-center">
                         <IconButton size="small" onClick={deleteRow(i)}>
                           <DeleteIcon fontSize="inherit" className="text-red-600"/>
                         </IconButton>
                       </td>
                     )}
                   </tr>
                 ))}
                 {rows.length===0 && (
                   <tr><td colSpan={edit?4:3}
                           className="p-3 text-center text-gray-400">(no holidays)</td></tr>
                 )}
               </tbody>
             </table>
           )}
         </div>
       </div>
     );
   }
   
   HolidayCalendar.propTypes = {
     apiBaseUrl: PropTypes.string,
     onClose:    PropTypes.func
   };
   